import { Injectable } from '@angular/core';
import { Observable, from, map, switchMap } from 'rxjs';
import { RepositoryBase } from '@asm/features/sync/repositories/repository-base';
import {
  OfflineOrderOutboxEntry,
  OutboxStatus,
  SyncOrderReceipt,
} from '@asm/features/sync/models/offline-order-sync.model';

@Injectable({ providedIn: 'root' })
export class OfflineOrderOutboxRepository extends RepositoryBase<OfflineOrderOutboxEntry> {
  protected readonly KEY_PATH = 'order_outbox';

  /** Entradas enviáveis: PENDING e FAILED (FAILED = retryable por padrão) */
  getPendingAndRetryable(b2bUnitUid: string, customerUid: string): Observable<OfflineOrderOutboxEntry[]> {
    return this.getAll().pipe(
      map(items =>
        items.filter(
          i =>
            i.b2bUnitUid === b2bUnitUid &&
            i.customerUid === customerUid &&
            (i.status === 'PENDING' || i.status === 'FAILED'),
        ),
      ),
    );
  }

  /** Todas as entradas não completamente reconciliadas — para exibição na lista */
  getVisibleForUnit(b2bUnitUid: string, customerUid: string): Observable<OfflineOrderOutboxEntry[]> {
    return this.getAll().pipe(
      map(items =>
        items
          .filter(i => i.b2bUnitUid === b2bUnitUid && i.customerUid === customerUid && !i.remoteObserved)
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
      ),
    );
  }

  /** Entradas aguardando processamento pelo Motor de Vendas (AWAITING_PROCESSING ou PROCESSING) */
  getAwaitingProcessing(b2bUnitUid: string, customerUid: string): Observable<OfflineOrderOutboxEntry[]> {
    return this.getAll().pipe(
      map(items =>
        items.filter(
          i =>
            i.b2bUnitUid === b2bUnitUid &&
            i.customerUid === customerUid &&
            (i.status === 'AWAITING_PROCESSING' || i.status === 'PROCESSING'),
        ),
      ),
    );
  }

  /** Transição para AWAITING_PROCESSING após receber 202 */
  markAsAwaitingProcessing(clientOrderId: string): Observable<OfflineOrderOutboxEntry> {
    return this.getByKey(clientOrderId).pipe(
      switchMap(entry => {
        if (!entry) throw new Error(`Outbox entry not found: ${clientOrderId}`);
        return this.addOrUpdate({ ...entry, status: 'AWAITING_PROCESSING' });
      }),
    );
  }

  /** Persiste o receipt e muda para PROCESSED / PROCESSED_WITH_CHANGES / PROCESSED_REJECTED */
  storeServerResult(clientOrderId: string, receipt: SyncOrderReceipt): Observable<OfflineOrderOutboxEntry> {
    return this.getByKey(clientOrderId).pipe(
      switchMap(entry => {
        if (!entry) throw new Error(`Outbox entry not found: ${clientOrderId}`);
        let status: OutboxStatus;
        if (!receipt.orderCode) {
          status = 'PROCESSED_REJECTED';
        } else if (receipt.hasChanges) {
          status = 'PROCESSED_WITH_CHANGES';
        } else {
          status = 'PROCESSED';
        }
        return this.addOrUpdate({ ...entry, status, receipt });
      }),
    );
  }

  /** Marca como reconciliado — projeção local pode ser retirada da lista */
  markAsRemoteObserved(clientOrderId: string): Observable<OfflineOrderOutboxEntry> {
    return this.getByKey(clientOrderId).pipe(
      switchMap(entry => {
        if (!entry) throw new Error(`Outbox entry not found: ${clientOrderId}`);
        return this.addOrUpdate({ ...entry, remoteObserved: true });
      }),
    );
  }

  /** Registra falha de envio ou processamento */
  markAsError(clientOrderId: string, errorMessage: string): Observable<OfflineOrderOutboxEntry> {
    return this.getByKey(clientOrderId).pipe(
      switchMap(entry => {
        if (!entry) throw new Error(`Outbox entry not found: ${clientOrderId}`);
        return this.addOrUpdate({
          ...entry,
          status: 'FAILED' as OutboxStatus,
          lastError: errorMessage,
          attempts: (entry.attempts ?? 0) + 1,
        });
      }),
    );
  }

  /**
   * Atualiza apenas o status de uma entrada do outbox.
   * Usado pelo polling de status para refletir AWAITING_PROCESSING / PROCESSING
   * na lista de pedidos offline sem aguardar a conclusão final.
   */
  updateStatus(clientOrderId: string, status: OutboxStatus): Observable<OfflineOrderOutboxEntry> {
    return this.getByKey(clientOrderId).pipe(
      switchMap(entry => {
        if (!entry) throw new Error(`Outbox entry not found: ${clientOrderId}`);
        if (entry.status === status) return [entry] as unknown as ReturnType<typeof this.addOrUpdate>;
        return this.addOrUpdate({ ...entry, status });
      }),
    );
  }
}
